/**
 * Single source of truth for the AP bill amounts that mean something (ALC-1708).
 *
 * A bill has exactly three amounts worth naming:
 *
 *   GROSS   `totalAmount` — the DOCUMENT TOTAL: what the vendor charged, taxes
 *           included, exactly as printed on the document ("Valor do Documento" on a
 *           boleto, "Valor Total da Nota" on an NF). Everything that answers "how much
 *           was this expense" reads gross: GL expense classification, cash-flow
 *           projection, the closing pack, document↔bill matching.
 *
 *   SUBTOTAL `subtotalAmount` — the document value before the itemized tax breakdown.
 *           Informational; equals the gross whenever the document adds no tax on top
 *           of its line values (the overwhelmingly common case for service NFs, whose
 *           taxes are included in the price). Never a payment or classification base
 *           on its own — only the gross resolver's fallback for legacy rows.
 *
 *   NET     `netPaymentAmount` — the cash obligation: gross plus late surcharges,
 *           minus withholdings the payer retains. Everything that answers "how much
 *           do we pay" reads net: payment schedules, settlement, bank dispatch,
 *           `isFullyPaid`.
 *
 * `taxTotalAmount` is an informational roll-up of the document's tax breakdown. Those
 * taxes are already inside the gross — never add `taxTotalAmount` to anything. On a
 * foreign bill it is functional like the rest; its foreign face lives in
 * `originalTaxTotalAmount` and the two are always one `provisionExchangeRate` apart
 * (see packages/services/src/currency-policy/AGENTS.md).
 *
 * Historically `totalAmount` was bimodal: document ingestion wrote the withholding-NET
 * into it while other writers wrote the gross, so consumers split into a gross-expecting
 * camp and a net-expecting camp, and each was wrong for the bills the other camp's
 * writer created. Both failure modes shipped: a bill whose `totalAmount` held the net
 * was provisioned with the net as its expense (withholding deducted twice), and a bill
 * whose `totalAmount` held the gross seeded a payment schedule that could never reach
 * zero. ALC-1708 pins `totalAmount` to the document total — every writer is converged
 * and existing net-storing rows are corrected by migration — so the column is kept, not
 * retired. What is retired is reading it raw: resolve through these helpers and the
 * gross/net question never arises.
 *
 * See ./AGENTS.md.
 */

/** Numeric-ish inputs: Prisma `Decimal`, a plain number, or a decimal string. */
export type BillAmountValue = number | string | bigint | { toString(): string } | null | undefined;

/** Amount semantics stay here; callers may supply their canonical precision policy. */
export interface BillAmountArithmetic<T> {
  zero: T;
  parse(value: BillAmountValue): T | null;
  add(left: T, right: T): T;
  subtract(left: T, right: T): T;
}

export interface BillGrossAmountSource {
  /** The document total. Authoritative — writers are converged on this meaning. */
  totalAmount?: BillAmountValue;
  /** Pre-tax-breakdown value; equals the gross when the document adds no tax. Fallback. */
  subtotalAmount?: BillAmountValue;
  /**
   * Last-resort alias for web payloads that carry a single amount. Beware: some web
   * list mappers set `amount` to the payable NET, so this only applies when the row
   * has no document total at all — never reorder it above the other two.
   */
  amount?: BillAmountValue;
}

export interface BillNetPayableSource extends BillGrossAmountSource {
  netPaymentAmount?: BillAmountValue;
  interestAmount?: BillAmountValue;
  fineAmount?: BillAmountValue;
  withholdingsIsRetained?: boolean | null;
  totalWithholdingAmount?: BillAmountValue;
  irrfWithholdingAmount?: BillAmountValue;
  issWithholdingAmount?: BillAmountValue;
  pisWithholdingAmount?: BillAmountValue;
  cofinsWithholdingAmount?: BillAmountValue;
  csllWithholdingAmount?: BillAmountValue;
  inssWithholdingAmount?: BillAmountValue;
  ivaWithholdingAmount?: BillAmountValue;
}

function toNumber(value: BillAmountValue): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(typeof value === 'string' ? value : value.toString());
  return Number.isFinite(parsed) ? parsed : null;
}

const numberArithmetic: BillAmountArithmetic<number> = {
  zero: 0,
  parse: toNumber,
  add: (left, right) => left + right,
  subtract: (left, right) => left - right,
};

/**
 * The bill's gross — the document total.
 *
 * `totalAmount` first: it is the enforced document total (taxes included), and it is
 * the only column that carries taxes a document adds on top of its line values, so a
 * subtotal-first read would under-state the expense on exactly those bills.
 * `subtotalAmount` is the fallback for legacy rows written before `totalAmount`
 * existed on every path; for every correctly-written bill the two agree unless the
 * document adds tax, in which case only `totalAmount` is right.
 *
 * A row with neither resolves to 0, which fails classification split-total validation
 * loudly instead of posting a wrong expense. That population is not purely historical:
 * card-item, card-fast-track and bank-fee creators were still producing amount-less
 * DRAFT bills and are fixed alongside this. Any new bill creator must populate
 * `totalAmount` with the document total.
 *
 * Withholdings never belong in any of these columns: a bill whose `totalAmount` holds
 * the withholding-net is corrupt data (the pre-ALC-1708 ingestion bug), corrected by
 * migration — do not re-introduce a writer that produces it.
 */
export function resolveBillGrossAmount(bill: BillGrossAmountSource): number {
  return resolveBillGrossAmountWithArithmetic(bill, numberArithmetic);
}

export function resolveBillGrossAmountWithArithmetic<T>(
  bill: BillGrossAmountSource,
  arithmetic: BillAmountArithmetic<T>,
): T {
  return (
    arithmetic.parse(bill.totalAmount) ??
    arithmetic.parse(bill.subtotalAmount) ??
    arithmetic.parse(bill.amount) ??
    arithmetic.zero
  );
}

export interface ApClassificationAmountSource {
  isReimbursement?: boolean | null;
  subtotalAmount?: BillAmountValue;
  totalAmount?: BillAmountValue;
  reimbursementRequest?: {
    approvedAmount?: BillAmountValue;
    requestedAmount?: BillAmountValue;
  } | null;
}

/**
 * Amount that AP classifications / provision postings allocate against.
 *
 * Reimbursements use the approved (else requested) request amount. Ordinary bills
 * use the GROSS — `totalAmount` first, then `subtotalAmount` for legacy rows
 * (ALC-1708). Returns `null` when no usable amount is present so callers can
 * skip a rescale instead of writing a zero split.
 */
export function resolveApClassificationTargetAmount(
  bill: ApClassificationAmountSource,
): number | null {
  const reimbursementAmount =
    bill.reimbursementRequest?.approvedAmount ?? bill.reimbursementRequest?.requestedAmount;
  if (bill.isReimbursement && reimbursementAmount != null) {
    return toNumber(reimbursementAmount);
  }

  return toNumber(bill.totalAmount) ?? toNumber(bill.subtotalAmount);
}

/**
 * Retained withholdings only.
 *
 * Deducts only on an explicit `withholdingsIsRetained === true`. The column is
 * non-nullable with a `false` default, so `undefined` cannot mean "unset on the row" —
 * it means the caller did not select it. Treating that as "retained" would under-state
 * the net payable by the withholding whenever a partial select omits the flag. No
 * evidence of retention, no deduction.
 */
export function resolveRetainedWithholdingTotal(bill: BillNetPayableSource): number {
  return resolveRetainedWithholdingTotalWithArithmetic(bill, numberArithmetic);
}

export function resolveRetainedWithholdingTotalWithArithmetic<T>(
  bill: BillNetPayableSource,
  arithmetic: BillAmountArithmetic<T>,
): T {
  if (bill.withholdingsIsRetained !== true) return arithmetic.zero;

  // `??`, not a zero check: a stored 0 is a real "nothing retained", and treating it as
  // unset would disagree with the incumbent derivation in bill-classification-validation.
  const explicit = arithmetic.parse(bill.totalWithholdingAmount);
  if (explicit !== null) return explicit;

  return [
    bill.irrfWithholdingAmount,
    bill.issWithholdingAmount,
    bill.pisWithholdingAmount,
    bill.cofinsWithholdingAmount,
    bill.csllWithholdingAmount,
    bill.inssWithholdingAmount,
    bill.ivaWithholdingAmount,
  ].reduce(
    (sum, value) => arithmetic.add(sum, arithmetic.parse(value) ?? arithmetic.zero),
    arithmetic.zero,
  );
}

/**
 * The bill's net payable — the cash obligation.
 *
 * A persisted `netPaymentAmount` wins: it is what the payment schedule was built from
 * and what a bank dispatch must honour, so recomputing it here could disagree with
 * money already in flight. Otherwise derive: gross + interest + fine − retained
 * withholdings.
 */
export function resolveBillNetPayable(bill: BillNetPayableSource): number {
  return resolveBillNetPayableWithArithmetic(bill, numberArithmetic);
}

export function resolveBillNetPayableWithArithmetic<T>(
  bill: BillNetPayableSource,
  arithmetic: BillAmountArithmetic<T>,
): T {
  const persisted = arithmetic.parse(bill.netPaymentAmount);
  if (persisted !== null) return persisted;

  const grossWithInterest = arithmetic.add(
    resolveBillGrossAmountWithArithmetic(bill, arithmetic),
    arithmetic.parse(bill.interestAmount) ?? arithmetic.zero,
  );
  return arithmetic.subtract(
    arithmetic.add(grossWithInterest, arithmetic.parse(bill.fineAmount) ?? arithmetic.zero),
    resolveRetainedWithholdingTotalWithArithmetic(bill, arithmetic),
  );
}

// The CFDI path writes "IEPS" or, for a code it cannot name, the SAT code itself; an extracted
// PDF may spell it "I.E.P.S." or "Impuesto especial sobre producción y servicios".
function isMexicanIeps(taxType: string | null | undefined): boolean {
  const normalized = (taxType ?? '').toUpperCase().replace(/[.\s]/g, '');
  return (
    normalized.startsWith('IEPS') || normalized === '003' || normalized.includes('IMPUESTOESPECIAL')
  );
}

// The CFDI path writes "IVA" (SAT 002); an extracted PDF may spell it "I.V.A.", "IVA 16%",
// "Impuesto: IVA 16%" or "Impuesto al valor agregado". A withheld IVA is not a traslado.
function isMexicanIva(taxType: string | null | undefined): boolean {
  const normalized = (taxType ?? '').toUpperCase().replace(/\./g, '').trim();
  return (
    (/(?:^|[^A-Z])IVA/.test(normalized) ||
      normalized === '002' ||
      /VALOR\s*AGREGADO/.test(normalized)) &&
    !normalized.includes('RET')
  );
}

/**
 * The roll-up a bill stores as `taxTotalAmount` from its document's tax breakdown, or null when no
 * line carries an amount (the breakdown states nothing to roll up).
 *
 * On a Mexican entity's bill `taxTotalAmount` is the IVA trasladado, which the provision debits to
 * IVA pendiente de acreditar, so only IVA lines count. Every other tax stays in the cost, inside
 * the gross: IEPS is not creditable to a buyer that does not resell the goods (LIEPS art. 4), and a
 * hotel's ISH or a foreign vendor's sales tax is not IVA. A Mexican document without IVA therefore
 * states a tax total of zero, not none. Elsewhere a Mexican IEPS traslado (SAT 003) is still left
 * out and every other tax counts.
 */
export function sumDocumentTaxBreakdown(
  breakdown: readonly { taxType?: string | null; amount?: number | null }[] | null | undefined,
  entityCountryCode?: string | null,
): number | null {
  const lines = (breakdown ?? []).filter((tax) => (tax.amount ?? 0) > 0);
  if (lines.length === 0) return null;
  const counts =
    entityCountryCode === 'MX'
      ? isMexicanIva
      : (taxType: string | null | undefined) => !isMexicanIeps(taxType);
  return lines.reduce((sum, tax) => (counts(tax.taxType) ? sum + (tax.amount ?? 0) : sum), 0);
}
